import {
	type ContextStore,
	type CreateContextStore,
	type NullableSurfacePlatformContext,
	type Selectors,
	type Subscription,
} from './types';

const createDiff = <T extends Record<string, string | number | boolean | null | undefined>>(
	left: Partial<T>,
	right: Partial<T>,
): Partial<T> =>
	Object.entries(left).reduce(
		(acc, [key, value]) => (right?.[key] !== value ? { ...acc, [key]: right?.[key] } : acc),
		{},
	);

const createSubscriberContext = <
	Context extends Record<string, string | number | boolean | null | undefined>,
>(
	context: Context,
	selectors: (keyof Context)[],
) =>
	selectors.reduce(
		(a, selector) => ({
			...a,
			[selector]: context?.[selector],
		}),
		{},
	);

const idenityMiddleware = <T>(context: T): T => context;

export const createContextStore: CreateContextStore = (config) => {
	const initialState = config?.initialState;
	const middleware = config?.middleware ?? idenityMiddleware;

	let state: NullableSurfacePlatformContext = middleware({
		tenantId: undefined,
		isSiteAdmin: undefined,
		product: undefined,
		subproduct: undefined,
		switcherMenuHasBeenOpened: undefined,
		sessionId: undefined,
		locale: undefined,
		orgId: undefined,
		workspaceId: undefined,
		...initialState,
	});

	const subscribers = new Map<Subscription, Selectors | undefined>();

	const updateSubscribers = (
		updatedContext: NullableSurfacePlatformContext,
		changes: Partial<NullableSurfacePlatformContext>,
	) => {
		const changedKeys = Object.keys(changes) as Selectors;

		subscribers.forEach((selectors, subscription) => {
			if (!selectors || changedKeys.find((changedKey) => selectors.includes(changedKey))) {
				updateSubscriber({
					subscription,
					context: updatedContext,
					subscriberContext: selectors
						? createSubscriberContext(updatedContext, selectors)
						: updatedContext,
					changes,
				});
			}
		});
	};

	const updateSubscriber = ({
		subscription,
		context,
		subscriberContext,
		changes = {},
	}: {
		subscription: Subscription;
		context: NullableSurfacePlatformContext;
		subscriberContext: Partial<NullableSurfacePlatformContext>;
		changes?: Partial<NullableSurfacePlatformContext>;
	}) => {
		subscription({ subscriberContext, changes, context });
	};

	const update: ContextStore['update'] = (callback) => {
		const newState = middleware(callback(state));
		updateSubscribers(newState, createDiff(state, newState));
		state = newState;
	};

	const subscribe: ContextStore['subscribe'] = (...newSubscribers) => {
		newSubscribers.forEach((newSubscriber) => {
			const [subscription, selectors, options] = newSubscriber;

			if (options?.updateOnSubscribe) {
				updateSubscriber({
					subscription,
					context: state,
					subscriberContext: selectors ? createSubscriberContext(state, selectors) : state,
				});
			}

			subscribers.set(subscription, selectors);
		});
	};

	const unsubscribe: ContextStore['unsubscribe'] = (subscription) => {
		!!subscription ? subscribers.delete(subscription) : subscribers.clear();
	};

	const getCurrentContext: ContextStore['getCurrentContext'] = () => state;

	return {
		update,
		subscribe,
		getCurrentContext,
		unsubscribe,
	};
};
